import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import * as changeCase from "change-case";

import {
  DistanceUnitType,
  calculateTapped,
  inputTypeOnChanged,
  inputValueOnChanged,
  outputTypeOnChanged,
  resultCopyTapped,
  selectState,
} from "./DistanceUnitConverterSlice";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { ResultCard } from "../../components/result_card/ResultCard";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { EqualColumnTable } from "../../components/equal_column_table/EqualColumnTable";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2";
import { HomeCard } from "../../components/home_card/HomeCard";
import WeightUnitConverterImage from "../../assets/WeightUnitConverter.webp";
import TemperatureConverterImage from "../../assets/TemperatureConverter.webp";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { useTranslation } from "react-i18next";

export function DistanceUnitConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("distanceConverter.title")}
      pageSeoTitle={t("distanceConverter.seoTitle")}
      pageDescription={t("distanceConverter.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("distanceConverter.mainHeading")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("distanceConverter.fromLabel")}
            value={state.inputUnit}
            onChange={(e) =>
              dispatch(
                inputTypeOnChanged(Number(e.target.value) as DistanceUnitType)
              )
            }
          >
            <MenuItem value={DistanceUnitType.millimeter}>
              {t("distanceConverter.options.millimeter")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.centimeter}>
              {t("distanceConverter.options.centimeter")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.meter}>
              {t("distanceConverter.options.meter")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.inch}>
              {t("distanceConverter.options.inch")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.feet}>
              {t("distanceConverter.options.feet")}
            </MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("distanceConverter.toLabel")}
            value={state.outputUnit}
            onChange={(e) =>
              dispatch(
                outputTypeOnChanged(Number(e.target.value) as DistanceUnitType)
              )
            }
          >
            <MenuItem value={DistanceUnitType.millimeter}>
              {t("distanceConverter.options.millimeter")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.centimeter}>
              {t("distanceConverter.options.centimeter")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.meter}>
              {t("distanceConverter.options.meter")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.inch}>
              {t("distanceConverter.options.inch")}
            </MenuItem>
            <MenuItem value={DistanceUnitType.feet}>
              {t("distanceConverter.options.feet")}
            </MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("distanceConverter.valueLabel")}
            variant="outlined"
            color="info"
            value={state.inputValue || ""}
            onChange={(e) =>
              dispatch(inputValueOnChanged(Number(e.target.value)))
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>{state.inputSymbol}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 1, mb: 3 }}>
          <CustomButton
            label={t("distanceConverter.convertButton")}
            onClick={() => {
              dispatch(calculateTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.outputValue}`}
            symbol={`${state.resultSymbol}`}
            description={changeCase.pascalCase(
              DistanceUnitType[state.resultUnit]
            )}
            onCopy={() => dispatch(resultCopyTapped())}
            resultCopied={state.resultCopied}
          />
        </Grid2>
        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("distanceConverter.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("distanceConverter.content.freeDescription")}
          </Typography>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("distanceConverter.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("distanceConverter.content.howToDescription")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("distanceConverter.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="weightConverter"
            link="/WeightUnitConverter"
            smallCard={true}
            image={WeightUnitConverterImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="temperatureConverter"
            link="/TemperatureConverter"
            smallCard={true}
            image={TemperatureConverterImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
